import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, MoreHorizontal, Pencil, Trash2, MapPin } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

const AdminMonuments = () => {
  const [monuments, setMonuments] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMonument, setEditingMonument] = useState(null);
  const [filterCity, setFilterCity] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    city_id: '',
    description: '',
    meeting_point_default: '',
    category_ids: [],
    image_url: '',
  });

  useEffect(() => {
    loadData();
  }, [filterCity]);

  const loadData = async () => {
    try {
      const [monumentsRes, citiesRes, categoriesRes] = await Promise.all([
        adminAPI.listMonuments(filterCity !== 'all' ? { city_id: filterCity } : {}),
        adminAPI.listCities(),
        adminAPI.listCategories(),
      ]);
      setMonuments(monumentsRes.data);
      setCities(citiesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMonument) {
        await adminAPI.updateMonument(editingMonument.id, formData);
        toast.success('Monument updated successfully');
      } else {
        await adminAPI.createMonument(formData);
        toast.success('Monument created successfully');
      }
      handleDialogClose();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save monument');
    }
  };

  const handleEdit = (monument) => {
    setEditingMonument(monument);
    setFormData({
      name: monument.name,
      city_id: monument.city_id,
      description: monument.description || '',
      meeting_point_default: monument.meeting_point_default || '',
      category_ids: monument.category_ids || [],
      image_url: monument.image_url || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (monumentId) => {
    if (!window.confirm('Are you sure you want to delete this monument?')) return;
    try {
      await adminAPI.deleteMonument(monumentId);
      toast.success('Monument deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete monument');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingMonument(null);
    setFormData({
      name: '',
      city_id: '',
      description: '',
      meeting_point_default: '',
      category_ids: [],
      image_url: '',
    });
  };

  const toggleCategory = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId]
    }));
  };

  return (
    <AdminLayout title="Monuments" subtitle="Manage monuments and attractions">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>All Monuments</CardTitle>
          <div className="flex items-center gap-4">
            <Select value={filterCity} onValueChange={setFilterCity}>
              <SelectTrigger className="w-40" data-testid="filter-city">
                <SelectValue placeholder="Filter by city" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map(city => (
                  <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700" 
              data-testid="create-monument-btn"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Monument
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Monument</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Meeting Point</TableHead>
                  <TableHead>Categories</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monuments.map((monument) => (
                  <TableRow key={monument.id} className="table-row-hover" data-testid={`monument-row-${monument.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {monument.image_url ? (
                          <img 
                            src={monument.image_url} 
                            alt={monument.name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                        <span className="font-medium">{monument.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{monument.city_name}</TableCell>
                    <TableCell className="text-slate-500">
                      {monument.meeting_point_default || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {monument.category_ids?.slice(0, 2).map(catId => {
                          const cat = categories.find(c => c.id === catId);
                          return cat ? (
                            <Badge key={catId} variant="outline" className="text-xs">
                              {cat.name}
                            </Badge>
                          ) : null;
                        })}
                        {monument.category_ids?.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{monument.category_ids.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`monument-actions-${monument.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(monument)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(monument.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {monuments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      No monuments found. Add your first monument to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog outside Card */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMonument ? 'Edit Monument' : 'Add New Monument'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Monument Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Colosseum"
                required
                data-testid="monument-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Select 
                value={formData.city_id} 
                onValueChange={(v) => setFormData({ ...formData, city_id: v })}
              >
                <SelectTrigger data-testid="monument-city-select">
                  <SelectValue placeholder="Select a city" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map(city => (
                    <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description"
                data-testid="monument-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Default Meeting Point (optional)</Label>
              <Input
                value={formData.meeting_point_default}
                onChange={(e) => setFormData({ ...formData, meeting_point_default: e.target.value })}
                placeholder="e.g., Main entrance"
                data-testid="monument-meeting-point-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Image URL (optional)</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
                data-testid="monument-image-url-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Categories (optional)</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-md">
                {categories.map(cat => (
                  <Badge 
                    key={cat.id}
                    variant={formData.category_ids.includes(cat.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleCategory(cat.id)}
                  >
                    {cat.name}
                  </Badge>
                ))}
                {categories.length === 0 && (
                  <span className="text-sm text-slate-500">No categories available</span>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-monument-btn">
                {editingMonument ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminMonuments;
